/**
 * Formatação e vocabulário.
 *
 * Os rótulos são os MESMOS do gerador de documentos
 * (`api/src/documentos/documento.service.ts`). Duplicar a lista foi decisão
 * consciente: o alternativo era um pacote compartilhado entre a API e a tela,
 * e o custo de manter esse pacote não se paga por vinte palavras. O que
 * garante que as duas listas não divirjam é o teste 10.9, que compara o
 * rótulo mostrado na tela com o rótulo impresso no PDF.
 *
 * As casas decimais são de APRESENTAÇÃO, não de armazenamento — a mesma
 * distinção que o documento faz: profundidade sai `1,00`, e não `1,500`
 * porque o banco guarda numeric(8,3).
 */

const FUSO = 'America/Sao_Paulo';

export const ROTULOS: Record<string, string> = {
  // Status do ensaio (migração 0001; a fila os exibe desde a 0037)
  programado: 'Programado', em_execucao: 'Em execução', pausado: 'Pausado',
  dados_completos: 'Dados completos', em_processamento: 'Em processamento',
  aguardando_verificacao: 'Aguardando verificação',
  devolvido_para_correcao: 'Devolvido para correção',
  cancelado: 'Cancelado',
  // `aprovado` e `substituido` já existem mais abaixo (aferição e baixa)
  // e valem para os dois contextos — repetir aqui só quebraria o build.
  // Situação do relato do beta (migração 0036)
  novo: 'Novo', 'em analise': 'Em análise', aceito: 'Aceito',
  resolvido: 'Resolvido', 'nao procede': 'Não procede',
  duplicado: 'Duplicado',
  // Acondicionamento de amostra DEFORMADA (migração 0014)
  saco_rafia: 'Saco de ráfia', saco_plastico: 'Saco plástico',
  spt: 'Amostra de SPT',
  // Acondicionamento de amostra INDEFORMADA (migração 0014)
  bloco: 'Bloco', shelby: 'Shelby', denison: 'Denison',
  anel: 'Anel', outro: 'Outro',
  // Acondicionamento de herança: some da tela, continua no acervo
  saco: 'Saco', caixa: 'Caixa', tubo_shelby: 'Tubo Shelby',
  bloco_indeformado: 'Bloco indeformado',
  recipiente: 'Recipiente', frasco: 'Frasco',
  corpo_de_prova: 'Corpo de prova',
  // Tipo de amostra
  deformada: 'Deformada', indeformada: 'Indeformada',
  cp_moldado: 'CP moldado pelo cliente',
  // Como chegou o CP moldado pelo cliente (0209)
  cp_no_molde: 'No molde', cp_desmoldado: 'Desmoldado',
  // Situação da amostra
  recebida: 'Recebida', aceita: 'Aceita',
  aceita_com_ressalva: 'Aceita com ressalva', rejeitada: 'Rejeitada',
  armazenada: 'Armazenada', em_ensaio: 'Em ensaio',
  consumida: 'Consumida', devolvida: 'Devolvida',
  descartada: 'Descartada', subdividida: 'Subdividida',
  // Decisão do recebimento
  aceite: 'Aceite', aceite_com_ressalva: 'Aceite com ressalva', rejeicao: 'Rejeição',
  // Origem do lote
  equipe_propria: 'Coleta por equipe própria',
  cliente: 'Entregue pelo cliente',
  empresa_sondagem: 'Empresa de sondagem',
  controle_obra: 'Controle de obra',
  // Roteiro fotográfico por amostra (migração 0010)
  embalagem_externa: 'Embalagem externa', acondicionamento: 'Acondicionamento',
  etiqueta_original: 'Etiqueta de identificação', amostra_aberta: 'Amostra aberta',
  // Fotografias do lote (migração 0015)
  conjunto: 'Amostras em conjunto', veiculo: 'Veículo / transportadora',
  recibo_entrega: 'Recibo de entrega',
  // Fotografias de herança (briefing, seção 7)
  lacre: 'Lacre', material: 'Material recebido', dano: 'Dano',
  contaminacao: 'Contaminação', vazamento: 'Vazamento',

  // ── Equipamentos (migração 0021) ──────────────────────────────────
  // O banco guarda o código sem acento; quem devolve o acento é esta
  // lista. "Picnometro" numa tela de laboratório é erro de português, e
  // erro de português numa tela técnica corrói a confiança no resto.
  disponivel: 'Disponível', reservado: 'Reservado', em_uso: 'Em uso',
  em_verificacao: 'Em verificação', em_calibracao: 'Em calibração',
  em_manutencao: 'Em manutenção', fora_de_tolerancia: 'Fora de tolerância',
  bloqueado: 'Bloqueado', inativo: 'Inativo', descartado: 'Descartado',

  // Situação do usuário (0034). "Desligado" é o que a Testesolo chamava
  // de "excluído": o acesso morre e o histórico fica (RN-163).
  desligado: 'Desligado', travado: 'Travado',
  balanca: 'Balança', picnometro: 'Picnômetro', estufa: 'Estufa',
  prensa: 'Prensa', cilindro: 'Cilindro', casagrande: 'Aparelho de Casagrande',
  peneira: 'Peneira', termometro: 'Termômetro', paquimetro: 'Paquímetro',
  cronometro: 'Cronômetro', relogio_comparador: 'Relógio comparador',
  anel_dinamometrico: 'Anel dinamométrico', soquete: 'Soquete',
  // Grandezas aferidas (`equipamento_valor`)
  volume: 'Volume', massa: 'Massa', altura: 'Altura', diametro: 'Diâmetro',
  area: 'Área', espessura: 'Espessura', capacidade: 'Capacidade',
  constante: 'Constante',
  // As larguras do anel de cisalhamento, que é quadrado (onda 260 · RN-224)
  largura_1: 'Largura 1', largura_2: 'Largura 2',
  // Grandezas medidas por sensor (`equipamento.grandeza_medida`)
  forca: 'Força', deslocamento: 'Deslocamento', pressao: 'Pressão',
  temperatura: 'Temperatura', tempo: 'Tempo', comprimento: 'Comprimento',
  // Sensores e padrões (migração 0030)
  celula_carga: 'Célula de carga', lvdt: 'LVDT',
  regua_potenciometrica: 'Régua potenciométrica',
  transdutor_pressao: 'Transdutor de pressão',
  micrometro: 'Micrômetro', cabecote_micrometrico: 'Cabeçote micrométrico',
  trena: 'Trena', peso_padrao: 'Peso padrão',
  // Papel do registro
  equipamento: 'Equipamento', componente: 'Sensor', padrao: 'Padrão',
  // Baixa
  inutilizado: 'Inutilizado', extraviado: 'Extraviado',
  substituido: 'Substituído', vendido: 'Vendido',
  // Análise crítica do certificado (migração 0029)
  adequado: 'Adequado ao uso',
  adequado_com_restricao: 'Adequado com restrição',
  nao_adequado: 'Não adequado',
  aceitacao_com_guarda: 'Aceitação com banda de guarda',
  aceitacao_simples: 'Aceitação simples',
  aprovado_com_restricao: 'Aprovado com restrição',
  // Resultado da aferição e da calibração
  aprovado: 'Aprovado', reprovado: 'Reprovado',
  aprovado_com_ressalva: 'Aprovado com ressalva',

  // ── Perfis de usuário ─────────────────────────────────────────────
  administrador: 'Administrador', direcao: 'Direção',
  gestor_operacional: 'Gestor operacional',
  gestor_lab: 'Gestor do laboratório', gestor_campo: 'Gestor de campo',
  responsavel_tecnico: 'Responsável técnico',
  coordenador_qualidade: 'Coordenador da qualidade',
  tecnico_lab: 'Técnico de laboratório', operador_campo: 'Operador de campo',
  auxiliar: 'Auxiliar', comercial: 'Comercial', auditor: 'Auditor',
  cliente_externo: 'Cliente externo', somente_leitura: 'Somente leitura',

  // ── Ordem de serviço (migração 0001, tela na 0038) ────────────────
  // Os três status da OS são independentes: campo concluído com
  // relatório em verificação e medição nem começada é estado normal.
  // Por isso três listas, e não uma.
  // Situação técnica
  rascunho: 'Rascunho', aguardando_aprovacao: 'Aguardando aprovação',
  aguardando_programacao: 'Aguardando programação', programada: 'Programada',
  aguardando_amostras: 'Aguardando amostras',
  amostras_recebidas: 'Amostras recebidas',
  ensaios_em_andamento: 'Ensaios em andamento',
  aguardando_dados: 'Aguardando dados',
  com_pendencia: 'Com pendência', concluida: 'Concluída',
  suspensa: 'Suspensa', cancelada: 'Cancelada',
  // Situação do relatório (`em_verificacao`, `com_pendencia` e `aprovado`
  // já estão acima e valem igual aqui — repetir quebraria o build)
  nao_iniciado: 'Não iniciado', em_elaboracao: 'Em elaboração',
  emitido: 'Emitido', disponibilizado: 'Disponibilizado ao cliente',
  // Situação da medição
  nao_iniciada: 'Não iniciada', em_medicao: 'Em medição',
  aguardando_conferencia: 'Aguardando conferência',
  aprovada: 'Aprovada', emitida: 'Emitida',
  // Prioridade
  baixa: 'Baixa', normal: 'Normal', alta: 'Alta', urgente: 'Urgente',
  // O Comercial (0501, onda 550): a linha de serviço da oportunidade e o tipo da tarefa.
  sondagem: 'Sondagem', laboratorio: 'Laboratório', controle_tecnologico: 'Controle tecnológico',
  investigacao_combinada: 'Investigação combinada', projetos_consultoria: 'Projetos e consultoria',
  acompanhamento: 'Acompanhamento', ligacao: 'Ligação', retorno: 'Retorno',
  confirmar_perda: 'Confirmar a perda',
};

/**
 * O acondicionamento depende do tipo (migração `0014`).
 *
 * A tela não pode oferecer uma lista só: saco de ráfia em amostra
 * indeformada é recusado pelo banco, e oferecer o que vai ser recusado é
 * fazer o técnico descobrir a regra pelo erro.
 */
export const ACONDICIONAMENTO: Record<string, readonly string[]> = {
  deformada: ['saco_rafia', 'saco_plastico', 'spt'],
  indeformada: ['bloco', 'shelby', 'denison', 'anel', 'outro'],
  /* CP moldado pelo cliente (0209, RN-212): como o corpo de prova chegou. */
  cp_moldado: ['cp_no_molde', 'cp_desmoldado', 'outro'],
};

/**
 * O rótulo do campo muda com o tipo da amostra — e o dado gravado não.
 *
 * "O termo que podemos utilizar para acondicionamento é método de coleta,
 * pois Bloco, Shelby, Denison, Anel ou outro são métodos de coleta"
 * (Testesolo, 23/08/2026).
 *
 * Está certo, e vale só metade: para a amostra INDEFORMADA a lista é de
 * métodos — o bloco é talhado, o Shelby é cravado, o Denison é rotativo, e
 * o método é o que decide se a estrutura do solo chegou preservada. Para a
 * DEFORMADA a lista é de embalagem mesmo: saco de ráfia, saco plástico,
 * amostrador do SPT. Chamar as duas de "método de coleta" trocaria um nome
 * impreciso por outro.
 *
 * A coluna do banco continua `tipo_acondicionamento`, e continua sendo a
 * mesma coluna. Renomear campo por causa de rótulo de tela é migração cara
 * que não muda nenhum dado — e o vocabulário do laudo já é gerado a partir
 * do valor, não do nome da coluna.
 */
/**
 * Cliente e obra, sem dizer a mesma coisa duas vezes.
 *
 * "Quando não tem nome da obra, apenas nome do cliente, não precisa
 * repetir o nome do cliente duas vezes" (Testesolo, 23/08/2026) — a lista
 * de amostras vinha com «Geoazimute / Geoazimute» em toda linha.
 *
 * Acontece porque a obra é cadastrada com o nome do cliente quando ainda
 * não se sabe o nome dela. Não é erro de cadastro: é o estado normal de
 * uma obra recém-aberta. Quem tem de lidar com isso é a tela.
 *
 * Devolve as duas linhas quando são diferentes, e uma só quando são a
 * mesma coisa — comparando sem acento, sem caixa e sem sufixo de empresa,
 * porque «Geoazimute» e «Geoazimute Ltda» também são a mesma coisa dita
 * duas vezes.
 */
export function clienteEObra(
  cliente: string | null | undefined,
  obra: string | null | undefined,
): string[] {
  const limpar = (v: string) => v
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\b(ltda|s\/?a|me|epp|eireli|engenharia|construcoes|construtora)\b/g, '')
    .replace(/[^a-z0-9]/g, '');
  const c = (cliente ?? '').trim();
  const o = (obra ?? '').trim();
  if (!c) return o ? [o] : [];
  if (!o) return [c];
  return limpar(c) === limpar(o) ? [c] : [c, o];
}

export function rotuloDoAcondicionamento(tipoAmostra: string | null | undefined): string {
  if (tipoAmostra === 'indeformada') return 'Método de coleta';
  /* O CP já foi moldado: a pergunta é se ele veio no molde ou desmoldado. */
  if (tipoAmostra === 'cp_moldado') return 'Como o CP chegou';
  return 'Acondicionamento';
}

export const TIPOS_AMOSTRA = ['deformada', 'indeformada', 'cp_moldado'] as const;

export function rotulo(v: unknown): string {
  const chave = String(v ?? '');
  if (!chave) return '';
  const conhecido = ROTULOS[chave];
  if (conhecido) return conhecido;
  const humano = chave.replace(/_/g, ' ');
  return humano.charAt(0).toUpperCase() + humano.slice(1);
}

/**
 * O dia sem hora nem fuso: `2026-09-10` → `10/09/2026`. A data do ensaio do
 * CPTu é um dia, e o `data()` (que passa por `new Date`) a leria como meia-noite
 * UTC e a mostraria um dia antes no fuso da casa (onda 312).
 */
export function dataDoDia(iso: string | null | undefined): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso ?? '');
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
}

/**
 * O dia puro (`AAAA-MM-DD`) como instante: meio-dia UTC, que é o mesmo dia em
 * Brasília (09h). `new Date('2026-10-01')` é meia-noite UTC — 21h do dia 30 no
 * fuso da casa —, e era assim que o filtro de período, a ficha da Fila e as
 * listas escreviam a data um dia antes (UI-T-02, onda 329).
 */
function instante(v: string | Date): Date {
  if (v instanceof Date) return v;
  return /^\d{4}-\d{2}-\d{2}$/.test(v) ? new Date(`${v}T12:00:00Z`) : new Date(v);
}

export function data(v: string | Date | null | undefined): string {
  if (!v) return '';
  if (typeof v === 'string') {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
    if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  }
  const d = instante(v);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('pt-BR', { timeZone: FUSO });
}

/**
 * A data no formato que `<input type="date">` aceita — `AAAA-MM-DD`.
 *
 * O driver entrega `date` do banco como carimbo ISO completo
 * (`2026-09-30T03:00:00.000Z`), e um campo de data alimentado com isso
 * fica em branco na tela e volta para a API como «Invalid date» (UX-063).
 * Converte no fuso da casa, para o dia não escorregar na virada da noite.
 */
export function dataIso(v: string | Date | null | undefined): string {
  if (!v) return '';
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
  const d = v instanceof Date ? v : new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  const partes = new Intl.DateTimeFormat('en-CA', {
    timeZone: FUSO, year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(d);
  const pega = (t: string) => partes.find((x) => x.type === t)?.value ?? '';
  return `${pega('year')}-${pega('month')}-${pega('day')}`;
}

/**
 * O dispositivo em uma linha — «Chrome 141 · Windows» (UX-043).
 *
 * A trilha guarda o user-agent inteiro, e deve: é o dado. Mas na ficha,
 * no celular, «Mozilla/5.0 (X11; Linux x86_64) AppleWebKit/537.36 (KHTML,
 * like Gecko) Chrome/141.0.0.0 Safari/537.36» ocupava três linhas por
 * movimentação. O resumo é para ler; o inteiro continua no `title`.
 */
export function dispositivoResumido(ua: string | null | undefined): string {
  if (!ua) return '';
  if (!/Mozilla\/|AppleWebKit|Gecko/.test(ua)) return ua;
  const nav = /Edg\/(\d+)/.exec(ua) ? `Edge ${/Edg\/(\d+)/.exec(ua)![1]}`
    : /OPR\/(\d+)/.exec(ua) ? `Opera ${/OPR\/(\d+)/.exec(ua)![1]}`
    : /SamsungBrowser\/(\d+)/.exec(ua) ? `Samsung Internet ${/SamsungBrowser\/(\d+)/.exec(ua)![1]}`
    : /Firefox\/(\d+)/.exec(ua) ? `Firefox ${/Firefox\/(\d+)/.exec(ua)![1]}`
    : /(?:Headless)?Chrome\/(\d+)/.exec(ua) ? `Chrome ${/(?:Headless)?Chrome\/(\d+)/.exec(ua)![1]}`
    : /Version\/(\d+).*Safari/.exec(ua) ? `Safari ${/Version\/(\d+)/.exec(ua)![1]}`
    : 'navegador';
  const so = /Android/.test(ua) ? 'Android'
    : /iPhone|iPad/.test(ua) ? 'iOS'
    : /Windows/.test(ua) ? 'Windows'
    : /Mac OS X/.test(ua) ? 'macOS'
    : /CrOS/.test(ua) ? 'ChromeOS'
    : /Linux/.test(ua) ? 'Linux' : '';
  return so ? `${nav} · ${so}` : nav;
}

export function dataHora(v: string | Date | null | undefined): string {
  if (!v) return '';
  const d = v instanceof Date ? v : new Date(v);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('pt-BR', {
    timeZone: FUSO,
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

/** A data de `v`, ou `null` quando não há data (ou ela é inválida). */
function comoData(v: string | Date | null | undefined): Date | null {
  if (!v) return null;
  const d = instante(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * «quinta, 08/10» — o dia da semana sem o «-feira» e o dia, no fuso da
 * casa. É a cabeça do Painel (spec 06b): o dia de hoje dito como se fala.
 */
export function diaDaSemanaEDia(v: string | Date | null | undefined): string {
  const d = comoData(v);
  if (!d) return '';
  const semana = d.toLocaleDateString('pt-BR', { weekday: 'long', timeZone: FUSO }).replace(/-feira$/, '');
  const dia = d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', timeZone: FUSO });
  return `${semana}, ${dia}`;
}

/** «07:42» — a hora e o minuto, no fuso da casa (o «atualizado às» do Painel). */
export function horaCurta(v: string | Date | null | undefined): string {
  const d = comoData(v);
  return d ? d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: FUSO }) : '';
}

/**
 * «08/10 07:42» — o dia e a hora sem o ano, no fuso da casa: para o que
 * aconteceu há pouco (a cápsula que «entrou» na estufa), onde o ano só
 * ocuparia lugar.
 */
export function diaEHoraCurtos(v: string | Date | null | undefined): string {
  const d = comoData(v);
  return d ? d.toLocaleString('pt-BR', {
    timeZone: FUSO, day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  }).replace(',', '') : '';
}

export function numero(v: unknown, casas: number, cortarZeros = false): string {
  if (v === null || v === undefined || v === '') return '';
  const n = Number(v);
  if (Number.isNaN(n)) return '';
  const s = n.toLocaleString('pt-BR', {
    minimumFractionDigits: cortarZeros ? 0 : casas,
    maximumFractionDigits: casas,
  });
  return s;
}

/**
 * Dinheiro (P-FMT-01, onda 552): «R$ 9.500,00» com duas casas, «R$ 85.000»
 * redondo. Entre o «R$» e o número vai o espaço INSEPARÁVEL (U+00A0): o
 * símbolo nunca fica numa linha e o número na outra (a 1280 px, «R$» em
 * cima e «11.000» embaixo no cartão do painel — T28-02).
 */
export const reais = (v: number, casas = 2) => `R$\u00a0${numero(v, casas)}`;
/** O valor redondo do cartão e da coluna do funil: «R$ 85.000» (os centavos ficam na ficha). */
export const reaisRedondos = (v: number) => reais(v, 0);

/**
 * Um valor com a unidade — ou o travessão, sem unidade solta.
 *
 * `{num(x)} %` com `x` vazio imprimia « %» e « p.p.» nas fichas ainda
 * sem resultado (184). Unidade sem número não é informação.
 */
export function comUnidade(v: unknown, casas: number, unidade: string): string {
  const n = numero(v, casas);
  return n === '' ? '—' : `${n} ${unidade}`;
}

/**
 * Um desvio, com o sinal na frente.
 *
 * Numa coluna de variação o sinal é metade da informação: `−2,04` e
 * `+3,10` contam histórias opostas sobre o mesmo estágio, e o `2,04`
 * que o `toLocaleString` escreve para o positivo faria as duas parecerem
 * a mesma coisa. O menos é o U+2212 e não o hífen — é o que alinha em
 * coluna de números.
 */
export function sinal(v: unknown, casas: number): string {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  const corpo = numero(Math.abs(n), casas);
  if (n === 0) return corpo;
  return `${n > 0 ? '+' : '−'}${corpo}`;
}

/**
 * A coordenada em graus decimais: «-27,491235; -48,651235» — seis casas (≈ 0,1 m), a vírgula decimal da casa e
 * o ponto e vírgula entre a latitude e a longitude (com a vírgula decimal, a vírgula entre as duas confundia).
 * Um formatador só para a ficha da vertical CPTu e a OS no Campo (agent-browser da 312). Vazio sem as duas.
 */
export function coordenada(lat: unknown, lon: unknown): string {
  const a = lat === null || lat === undefined || lat === '' ? '' : numero(lat, 6);
  const b = lon === null || lon === undefined || lon === '' ? '' : numero(lon, 6);
  return a && b ? `${a}; ${b}` : '';
}

/** Profundidade impressa como o documento da Testesolo escreve: `1,00 – 1,45`. */
export function faixaProfundidade(ini: unknown, fim: unknown): string {
  const a = numero(ini, 2);
  const b = numero(fim, 2);
  if (a && b) return `${a} – ${b}`;
  return a || b || '';
}

/**
 * Aceita vírgula onde o teclado do celular oferece vírgula.
 *
 * O teclado numérico do Android entrega `,` e o `<input type="number">`
 * devolve string vazia para "1,45". Sem esta conversão, a profundidade
 * digitada some silenciosamente — e some justamente o campo que o documento
 * precisa.
 */
export function paraNumero(v: string | null | undefined): number | undefined {
  /* Campo que ainda não foi tocado chega como `undefined` — a peneira sem
     massa digitada derrubava a tela inteira da granulometria (184). */
  if (v == null) return undefined;
  let limpo = String(v).trim().replace(/\s/g, '');
  if (!limpo) return undefined;
  /*
   * Ponto é separador de MILHAR — mas só quando há vírgula.
   *
   * Em pt-BR "1.234,5" é mil duzentos e trinta e quatro e meio, e sem
   * esta regra ele virava 1,2345: o valor entrava no banco mil vezes
   * menor, sem erro nenhum na tela. A condição importa tanto quanto a
   * regra: quem digita "1.5" num teclado de notebook quer um e meio, e
   * remover o ponto ali faria quinze. A vírgula é o que diz qual dos
   * dois mundos está sendo usado.
   */
  if (limpo.includes(',')) limpo = limpo.replace(/\./g, '');
  limpo = limpo.replace(',', '.');
  const n = Number(limpo);
  return Number.isFinite(n) ? n : undefined;
}

/**
 * Um número vindo do BANCO, pronto para ser editado num campo.
 *
 * `numeric(10,3)` chega da API como a string `"1.000"`, e um formulário
 * que a joga direto num `<input>` mostra ao brasileiro o que ele lê como
 * MIL. Foi o que aconteceu com «Pressão de assentamento (kPa)»: o ensaio
 * tinha 1 kPa e a tela dizia 1.000.
 *
 * Aqui o valor volta na notação de quem digita: vírgula decimal, sem
 * separador de milhar (que atrapalharia mais do que ajuda num campo de
 * digitação), e sem os zeros à direita que o tipo da coluna carrega mas
 * ninguém digitou.
 *
 * Não é o mesmo que `numero()`: aquele formata para LER — casas fixas,
 * milhar com ponto. Este formata para EDITAR, e o que sai dele tem de
 * poder voltar por `paraNumero` sem mudar de valor.
 */
export function paraCampo(v: unknown, casas = 6): string {
  if (v === null || v === undefined || v === '') return '';
  const n = Number(v);
  if (!Number.isFinite(n)) return '';
  const s = n.toFixed(casas).replace(/0+$/, '').replace(/\.$/, '');
  return s.replace('.', ',');
}

/**
 * Notação de engenharia — `1,86 × 10⁻²`.
 *
 * O cv de um solo mole anda por 10⁻³ e o de uma areia siltosa por 10⁻¹;
 * escrever `0,0018` e `0,186` numa coluna faz o leitor contar zeros para
 * comparar dois estágios, que é exatamente a comparação que ele está
 * tentando fazer. O laudo já imprime assim (`adensamento-documento`), e
 * a tela passa a falar a mesma língua.
 */
export function cientifica(v: unknown, casas = 2): string {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  if (n === 0) return '0';
  let exp = Math.floor(Math.log10(Math.abs(n)));
  let mant = n / 10 ** exp;
  /* A mantissa que arredonda para 10 sobe a potência: 9,996 × 10⁻⁹ é «1,00 × 10⁻⁸», nunca «10,00 × 10⁻⁹» (o
     `cientificaBr` do visual do CPTu já fazia assim — rodada final da 313). */
  if (Math.abs(Number(mant.toFixed(casas))) >= 10) {
    exp += 1;
    mant = n / 10 ** exp;
  }
  const sobrescrito = String(exp)
    .replace('-', '⁻')
    .replace(/[0-9]/g, (c) => '⁰¹²³⁴⁵⁶⁷⁸⁹'[Number(c)]);
  return `${numero(mant, casas)} × 10${sobrescrito}`;
}

/**
 * Um mês, escrito como mês (`0117`).
 *
 * A data de fabricação guarda o primeiro do mês por convenção de
 * armazenamento — o dia 1 é fixo, nunca foi lido de lugar nenhum, e
 * imprimi-lo transformaria convenção em informação. Sai `04/2019`.
 *
 * Recebe `AAAA-MM-DD` ou `AAAA-MM` e corta pelo texto, de propósito:
 * `new Date('2019-04-01')` é meia-noite UTC, que em Blumenau ainda é
 * março.
 */
export function mesAno(v: unknown): string {
  const s = String(v ?? '');
  const m = /^(\d{4})-(\d{2})/.exec(s);
  return m ? `${m[2]}/${m[1]}` : '';
}

/** Hoje no formato que `<input type="date">` espera, no fuso de Blumenau. */
export function hojeIso(): string {
  const agora = new Date();
  const partes = new Intl.DateTimeFormat('en-CA', {
    timeZone: FUSO, year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(agora);
  return partes;
}

/**
 * Ordem alfabética do jeito que se lê em português (onda 352, RN-273):
 * «Balão» antes de «Balança», «Cápsulas» junto de «Capsulas», sem
 * diferença entre maiúscula e minúscula.
 */
export const porNome = (a: string, b: string) =>
  a.localeCompare(b, 'pt-BR', { sensitivity: 'base', numeric: true });

export const plural = (n: number, um: string, muitos: string) =>
  `${n} ${n === 1 ? um : muitos}`;

/**
 * O número de telefone como se lê (onda 552): «5547999991234» →
 * «+55 (47) 99999-1234», o fixo «554730372817» → «+55 (47) 3037-2817», o
 * «08003037000» → «0800 303 7000» e o de fora do Brasil «+<dígitos>». Só a
 * APRESENTAÇÃO: o banco, a busca, o link do wa.me e o campo editável seguem
 * com os dígitos. O que não é só dígito (já digitado com máscara, um LID, um
 * e-mail) volta como veio. `nacional`: para o campo «Telefone», digitado à
 * mão — 10 ou 11 dígitos sem o 55 são o DDD e o número, no Brasil; o
 * WhatsApp vem sempre com o DDI, e lá 10 ou 11 dígitos são de fora.
 */
export function telefoneLegivel(n: string | null | undefined, opcoes: { nacional?: boolean } = {}): string {
  if (!n) return '';
  const s = n.trim();
  if (!/^\+?\d{8,15}$/.test(s)) return n;
  const d = s.replace(/^\+/, '');
  const o = /^0(800|300|500|900)(\d{3})(\d{4})$/.exec(d);
  if (o) return `0${o[1]} ${o[2]} ${o[3]}`;
  const br = opcoes.nacional && /^[1-9][1-9]\d{8,9}$/.test(d) ? `55${d}` : d;
  const m = /^55([1-9][1-9])(\d{4,5})(\d{4})$/.exec(br);
  if (m) return `+55 (${m[1]}) ${m[2]}-${m[3]}`;
  return `+${d}`;
}

/**
 * O telefone como foi digitado no cadastro («(47) 99104-5521», «47 3333.1200»),
 * escrito como se lê: tirada a máscara, vai ao `telefoneLegivel` nacional
 * («+55 (47) 99104-5521»). Texto que não é telefone volta como veio (onda
 * 554, Tarefa 8).
 */
export function telefoneDigitadoLegivel(t: string | null | undefined): string {
  if (!t) return '';
  return /^[+()\d .-]+$/.test(t.trim()) ? telefoneLegivel(t.replace(/[^\d+]/g, ''), { nacional: true }) : t;
}

/**
 * O nome do contato que o banco escreveu com o número (o WhatsApp não disse
 * o nome; migração 0506): o número legível. Um nome de verdade volta como veio.
 */
export const nomeDoContato = (nome: string | null | undefined): string =>
  (nome && /^\+?\d{8,15}$/.test(nome.trim()) ? telefoneLegivel(nome) : nome ?? '');

/**
 * O texto pronto da API com o número do contato dentro (o título do Radar,
 * «5547999991234 · WhatsApp»): só o celular ou o fixo brasileiro com o DDI,
 * solto entre não-dígitos, vira o legível.
 */
export const comTelefoneLegivel = (texto: string): string =>
  texto.replace(/\b55[1-9][1-9]\d{8,9}\b/g, (n) => telefoneLegivel(n));

/**
 * Só a palavra do plural, sem o número (P-TXT-11): para quando o número é
 * escrito de outro jeito — com sinal, com vírgula — ou mora noutro lugar
 * da tela, como a unidade do Andamento do Painel («+1 ensaio», «1,5 dias»).
 * O sinal não conta: «−1 ensaio» é singular.
 */
export const pluralSemNumero = (n: number, um: string, muitos: string) =>
  (Math.abs(n) === 1 ? um : muitos);


// ══ A trilha do ensaio por extenso (onda 323, revisão final) ══════════
//
// A trilha (`GET /ensaios/:id/trilha`) fala a língua do banco: tabela,
// coluna, INSERT/UPDATE. A tela fala a do laboratório (P-TXT-03, P-TXT-01):
// «lançou o ponto do LL», «Úmida + cápsula: 23,80 g». Um dicionário só,
// aqui junto do ROTULOS, para a tabela, a coluna e a operação. O que não
// está nele cai no `rotulo()` de sempre.

/** A tabela da trilha → o registro por extenso, com o artigo. */
export const TABELAS_DA_TRILHA: Record<string, string> = {
  ensaio: 'o ensaio',
  ensaio_tentativa: 'a tentativa',
  ensaio_status_historico: 'a situação do ensaio',
  ensaio_pendencia_justificada: 'a justificativa de pendência',
  ensaio_umidade: 'as Condições da umidade',
  ensaio_limite_liquidez: 'as Condições do LL',
  ensaio_limite_plasticidade: 'as Condições do LP',
  ensaio_granulometria: 'as Condições da granulometria',
  ensaio_massa_especifica_solidos: 'as Condições da massa específica',
  ensaio_compactacao: 'as Condições da compactação',
  ensaio_isc: 'as Condições do ISC',
  umidade_determinacao: 'a cápsula da umidade',
  limite_liquidez_ponto: 'o ponto do LL',
  limite_liquidez_ajuste: 'o ajuste do ponto do LL',
  limite_plasticidade_determinacao: 'a determinação do LP',
  granulometria_umidade: 'a cápsula da umidade higroscópica',
  granulometria_peneira: 'a peneira',
  granulometria_sedimentacao: 'a leitura do densímetro',
  mes_umidade: 'a cápsula da umidade da massa específica',
  mes_determinacao: 'a determinação do picnômetro',
  compactacao_ponto: 'o ponto da compactação',
  compactacao_umidade: 'a cápsula do ponto da compactação',
  isc_corpo_prova: 'o corpo de prova do ISC',
  isc_expansao_leitura: 'a leitura de expansão',
  isc_penetracao_leitura: 'a leitura de penetração',
};

/**
 * A coluna da trilha → o rótulo com acento, e como mostrar o valor. A ordem
 * é a de leitura: num lançamento, os primeiros campos com valor contam a
 * linha («Golpes: 35 · Úmida + cápsula: 23,80 g»).
 */
export const COLUNAS_DA_TRILHA: Record<string, { rotulo: string; casas?: number; unidade?: string }> = {
  situacao: { rotulo: 'Situação' },
  status: { rotulo: 'Situação' },
  verificacao: { rotulo: 'Verificação' },
  numero: { rotulo: 'Tentativa', casas: 0 },
  golpes: { rotulo: 'Golpes', casas: 0 },
  fase: { rotulo: 'Fase' },
  abertura_mm: { rotulo: 'Abertura', casas: 3, unidade: 'mm' },
  massa_retida: { rotulo: 'Massa retida', casas: 2, unidade: 'g' },
  tempo_s: { rotulo: 'Tempo', casas: 0, unidade: 's' },
  leitura: { rotulo: 'Leitura', casas: 4 },
  temperatura: { rotulo: 'Temperatura', casas: 1, unidade: '°C' },
  capsula_equipamento_id: { rotulo: 'Cápsula' },
  cilindro_equipamento_id: { rotulo: 'Cilindro' },
  picnometro_equipamento_id: { rotulo: 'Picnômetro' },
  peneira_equipamento_id: { rotulo: 'Peneira' },
  massa_cilindro_solo: { rotulo: 'Cilindro + solo', casas: 1, unidade: 'g' },
  massa_capsula_solo_umido: { rotulo: 'Úmida + cápsula', casas: 2, unidade: 'g' },
  massa_capsula_solo_seco: { rotulo: 'Seca + cápsula', casas: 2, unidade: 'g' },
  massa_amostra_umida: { rotulo: 'Amostra úmida', casas: 2, unidade: 'g' },
  massa_picnometro_solo_agua: { rotulo: 'M₂', casas: 2, unidade: 'g' },
  massa_picnometro_agua_declarada: { rotulo: 'M₁ declarado', casas: 2, unidade: 'g' },
  certificado_m1: { rotulo: 'Certificado do M₁' },
  motivo_m1_declarado: { rotulo: 'Motivo do M₁ declarado' },
  ponto_id: { rotulo: 'Ponto da compactação' },
  imersao_inicio: { rotulo: 'Início da imersão' },
  imersao_fim: { rotulo: 'Fim da imersão' },
  momento: { rotulo: 'Momento' },
  leitura_mm: { rotulo: 'Leitura', casas: 3, unidade: 'mm' },
  penetracao_mm: { rotulo: 'Penetração', casas: 2, unidade: 'mm' },
  carga_n: { rotulo: 'Carga', casas: 1, unidade: 'N' },
  leitura_anel_mm: { rotulo: 'Leitura do anel', casas: 3, unidade: 'mm' },
  origem: { rotulo: 'Origem' },
  golpes_adotados: { rotulo: 'Golpes adotados', casas: 0 },
  umidade_adotada: { rotulo: 'Umidade adotada', casas: 2, unidade: '%' },
  fora_do_ajuste: { rotulo: 'Fora do ajuste' },
  descartado: { rotulo: 'Descartado' },
  descartada: { rotulo: 'Descartada' },
  motivo_descarte: { rotulo: 'Motivo do descarte' },
  balanca_seca_equipamento_id: { rotulo: 'Balança da seca' },
  balanca_equipamento_id: { rotulo: 'Balança' },
  balanca_fina_equipamento_id: { rotulo: 'Balança do fino' },
  balanca_umidade_equipamento_id: { rotulo: 'Balança da umidade' },
  aparelho_equipamento_id: { rotulo: 'Aparelho de Casagrande' },
  densimetro_equipamento_id: { rotulo: 'Densímetro' },
  soquete_equipamento_id: { rotulo: 'Soquete' },
  anel_equipamento_id: { rotulo: 'Anel dinamométrico' },
  preparacao: { rotulo: 'Preparação' },
  nao_liquido: { rotulo: 'Não líquido (NL)' },
  nao_plastico: { rotulo: 'Não plástico (NP)' },
  temperatura_secagem: { rotulo: 'Temperatura de secagem', casas: 0, unidade: '°C' },
  horas_estufa: { rotulo: 'Horas na estufa', casas: 0, unidade: 'h' },
  fracao: { rotulo: 'Fração' },
  massa_total_umida: { rotulo: 'Massa total úmida', casas: 2, unidade: 'g' },
  massa_parcial_umida: { rotulo: 'Massa parcial úmida', casas: 2, unidade: 'g' },
  umidade_higroscopica_informada: { rotulo: 'Umidade higroscópica informada', casas: 2, unidade: '%' },
  com_sedimentacao: { rotulo: 'Com sedimentação' },
  massa_especifica_informada: { rotulo: 'Massa específica informada', casas: 3, unidade: 'g/cm³' },
  energia: { rotulo: 'Energia' },
  cilindro: { rotulo: 'Cilindro' },
  reuso_material: { rotulo: 'Reúso do material' },
  n_camadas: { rotulo: 'Camadas', casas: 0 },
  n_golpes: { rotulo: 'Golpes por camada', casas: 0 },
  massa_especifica_graos: { rotulo: 'Massa específica dos grãos', casas: 3, unidade: 'g/cm³' },
  energia_autorizada_por: { rotulo: 'Energia autorizada por' },
  energia_justificativa: { rotulo: 'Justificativa da energia' },
  compactacao_ensaio_id: { rotulo: 'Compactação de origem' },
  sobrecarga_g: { rotulo: 'Sobrecarga', casas: 0, unidade: 'g' },
  dias_imersao: { rotulo: 'Dias de imersão', casas: 0 },
  velocidade_mm_min: { rotulo: 'Velocidade', casas: 2, unidade: 'mm/min' },
  diametro_pistao_mm: { rotulo: 'Diâmetro do pistão', casas: 1, unidade: 'mm' },
  ajuste_somente_norma: { rotulo: 'Ajuste só nas penetrações da norma' },
  criterio_projeto: { rotulo: 'Critério de projeto' },
  isc_adotado: { rotulo: 'ISC adotado', casas: 2, unidade: '%' },
  isc_adotado_por: { rotulo: 'ISC adotado por' },
  reta_modo: { rotulo: 'Reta' },
  reta_n1: { rotulo: 'Golpes da 1ª alça', casas: 0 },
  reta_w1: { rotulo: 'Umidade da 1ª alça', casas: 2, unidade: '%' },
  reta_n2: { rotulo: 'Golpes da 2ª alça', casas: 0 },
  reta_w2: { rotulo: 'Umidade da 2ª alça', casas: 2, unidade: '%' },
  interpretacao_motivo: { rotulo: 'Motivo da interpretação' },
  interpretado_por: { rotulo: 'Interpretado por' },
  interpretado_em: { rotulo: 'Interpretado em' },
  interpretacao_a_rever: { rotulo: 'Interpretação a rever' },
  codigo: { rotulo: 'Pendência' },
  texto_na_epoca: { rotulo: 'Texto da pendência' },
  motivo: { rotulo: 'Motivo' },
  observacoes: { rotulo: 'Observações' },
  observacao: { rotulo: 'Observação' },
  data_execucao: { rotulo: 'Data de execução' },
  data_prevista: { rotulo: 'Data prevista' },
  instrucao: { rotulo: 'Instrução' },
  retrato: { rotulo: 'Retrato da ficha' },
  aberta_por: { rotulo: 'Aberta por' },
  adotada_por: { rotulo: 'Adotada por' },
  encerrada_por: { rotulo: 'Encerrada por' },
  umido_por: { rotulo: 'Úmida pesada por' },
  seco_por: { rotulo: 'Seca pesada por' },
  corrigido_por: { rotulo: 'Corrigido por' },
  justificado_por: { rotulo: 'Justificado por' },
};

/** Campos que dizem a linha, mas não o que se mediu (num lançamento, ficam de fora). */
const TECNICOS_DA_TRILHA = /^(id|ensaio_id|ensaio_isc_id|corpo_prova_id|tentativa|ordem|metodo_id|os_id|amostra_id)$|_em$|_por$/;

export const rotuloDaTabela = (t: string): string =>
  TABELAS_DA_TRILHA[t] ?? rotulo(t).toLowerCase();

export const rotuloDaColuna = (c: string): string =>
  COLUNAS_DA_TRILHA[c]?.rotulo ?? rotulo(c);

/** O valor de um campo da trilha como a tela mostra (P-FMT-01/04). */
export function valorDaTrilha(coluna: string, v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'sim' : 'não';
  if (typeof v === 'object') return 'registrado';
  const def = COLUNAS_DA_TRILHA[coluna];
  const s = String(v);
  if (typeof v === 'number' || /^-?\d+(\.\d+)?$/.test(s)) {
    const n = Number(s);
    const casas = def?.casas ?? (Number.isInteger(n) ? 0 : 2);
    const txt = numero(n, casas);
    return def?.unidade ? `${txt} ${def.unidade}` : txt;
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return dataDoDia(s);
  if (/^\d{4}-\d{2}-\d{2}T/.test(s)) return dataHora(s);
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(s)) return 'registro vinculado';
  return /^[a-z_]+$/.test(s) ? rotulo(s) : s;
}

/** O que a linha da trilha fez, por extenso: «lançou», «descartou», «adotou a repetição». */
export function acaoDaTrilha(l: {
  tabela: string; operacao: string; campos?: string[] | null;
  antes?: Record<string, unknown> | null; depois?: Record<string, unknown> | null;
}): string {
  const depois = l.depois ?? {};
  if (l.tabela === 'ensaio_status_historico') return 'mudou a situação do ensaio';
  if (l.tabela === 'ensaio_tentativa') {
    if (l.operacao === 'INSERT') {
      return depois.situacao === 'aberta' ? 'abriu a repetição'
        : `registrou a tentativa ${depois.numero ?? ''} como ${depois.situacao === 'vigente' ? 'vigente' : 'encerrada'}`.trim();
    }
    switch (depois.situacao) {
      case 'encerrada': return 'encerrou a tentativa, adotando a repetição';
      case 'vigente': return 'adotou a repetição';
      case 'desistida': return 'desistiu da repetição';
      default: return 'alterou a tentativa';
    }
  }
  const alvo = rotuloDaTabela(l.tabela);
  if (l.operacao === 'INSERT') return `lançou ${alvo}`;
  if (l.operacao === 'DELETE') return `apagou ${alvo}`;
  const campos = l.campos ?? [];
  if (campos.some((c) => /^descartad[oa]$/.test(c)) && (depois.descartado === true || depois.descartada === true)) {
    return `descartou ${alvo}`;
  }
  if (campos.some((c) => /sec[oa]/.test(c))) return `pesou a seca de ${alvo}`;
  return `alterou ${alvo}`;
}

/** «Golpes: 25 → 24» numa alteração; «Golpes: 35 · Úmida + cápsula: 23,80 g» num lançamento. */
export function oQueMudouNaTrilha(l: {
  operacao: string; campos?: string[] | null;
  antes?: Record<string, unknown> | null; depois?: Record<string, unknown> | null;
}, maximo = 4): string {
  const depois = l.depois ?? {};
  const antes = l.antes ?? {};
  const campos = l.operacao === 'UPDATE'
    ? (l.campos ?? [])
    : Object.keys(COLUNAS_DA_TRILHA).filter((c) => !TECNICOS_DA_TRILHA.test(c)
      && depois[c] !== null && depois[c] !== undefined && depois[c] !== '' && depois[c] !== false);
  if (!campos.length) return '';
  const partes = campos.slice(0, maximo).map((c) => (l.operacao === 'UPDATE'
    ? `${rotuloDaColuna(c)}: ${valorDaTrilha(c, antes[c])} → ${valorDaTrilha(c, depois[c])}`
    : `${rotuloDaColuna(c)}: ${valorDaTrilha(c, depois[c])}`));
  return partes.join(' · ') + (campos.length > maximo ? ` · e mais ${campos.length - maximo}` : '');
}
